<template>
  <div v-if="visibleOrderTypes && visibleOrderTypes.length > 1" class="wc-desktop-ot-switcher">
    <span class="wc-desktop-ot-switcher__label">{{ $t('shoppingAs') }}</span>
    <button
      v-for="ot in visibleOrderTypes"
      :key="ot.id"
      class="wc-desktop-ot-switcher__btn"
      :class="{ 'is-active': currentOrderType?.id === ot.id }"
      type="button"
      :aria-current="currentOrderType?.id === ot.id ? 'true' : undefined"
      :disabled="switching"
      @click="selectOrderType(ot)"
    >
      {{ ot.name }}
    </button>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import useOrderTypes from '@/composables/useOrderTypes';
import type OrderType from '@/models/entities/cart/order-type';

const { visibleOrderTypes, currentOrderType, navigateToOrderTypeHome } = useOrderTypes();
const switching = ref(false);
const selectOrderType = async (ot: OrderType): Promise<void> => {
  switching.value = true;
  try {
    await navigateToOrderTypeHome(ot.id);
  } finally {
    switching.value = false;
  }
};
</script>

<style scoped lang="scss">
@import '~@/assets/styles/tools';
@import '~@/assets/styles/settings';

.wc-desktop-ot-switcher {
  display: flex;
  flex-direction: row;
  align-items: stretch;
  background: var(--primary-darker-3);
  padding: 0 convert-px-to-rem(40);
  border-bottom: 0.0625rem solid rgba(255, 255, 255, 0.1);
  flex-wrap: wrap;
}

.wc-desktop-ot-switcher__label {
  font-size: convert-px-to-rem(11);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05rem;
  color: var(--white, $white);
  padding: 0.5625rem 1rem 0.5625rem 0;
  flex-shrink: 0;
  white-space: nowrap;
}

.wc-desktop-ot-switcher__btn {
  display: flex;
  align-items: center;
  font-size: convert-px-to-rem(13);
  padding: 0.5625rem 1rem;
  color: rgba(255, 255, 255, 0.7);
  border: none;
  border-bottom: 0.125rem solid transparent;
  background: none;
  cursor: pointer;
  white-space: nowrap;
  line-height: 1;

  &:hover {
    color: var(--white, $white);
    background: rgba(255, 255, 255, 0.06);
  }

  &.is-active {
    color: var(--white, $white);
    font-weight: 600;
    border-bottom-color: $active-ordertype-desktop;
    background: rgba(255, 255, 255, 0.08);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
}
</style>
